// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Handicap-stroke allocation, mirroring services/shared/Goffy.Scoring/HandicapStrokes.cs
// EXACTLY. The server owns these rules (CLAUDE.md §12); the frontend only uses them as a
// display fallback before server values arrive, and as the offline preview for the score
// currently being entered. Any change here must be mirrored in the C# original.
//
// Parity notes against the old inline copies:
// - Math.trunc, not Math.floor: C# integer division truncates toward zero, so a plus
//   handicap of -20 is base -1, not -2.
// - The handicap is rounded to an integer first, with Math.round to match how
//   round-init derives the stored SHCP (features/round-init/handicap.ts) — the server
//   only ever sees that int, so a raw fractional handicap index must not allocate
//   differently than its rounded SHCP would.
// - A non-positive stroke index never earns the extra remainder stroke, but base strokes
//   still apply.

/** Strokes received on a hole; plus handicaps give strokes back at -18 and beyond. */
export const strokesReceived = (playingHandicap: number, strokeIndex: number | null | undefined): number => {
  const handicap = Math.round(playingHandicap);
  const base = Math.trunc(handicap / 18);
  const remainder = handicap % 18;
  const extra = strokeIndex !== null && strokeIndex !== undefined && strokeIndex > 0 && strokeIndex <= remainder ? 1 : 0;
  return base + extra;
};

/**
 * As strokesReceived, but plus-handicap players get 0 strokes instead of giving strokes
 * back — team/match formats and the displayed scorecard (decision 2026-08-12).
 */
export const strokesReceivedClampedAtScratch = (
  playingHandicap: number,
  strokeIndex: number | null | undefined
): number => strokesReceived(Math.max(0, playingHandicap), strokeIndex);

/** True net strokes, unclamped — 0 or negative is meaningful for Stableford points. */
export const netStrokes = (
  grossStrokes: number | null | undefined,
  pickup: boolean,
  received: number
): number | null => {
  if (pickup || grossStrokes === null || grossStrokes === undefined) return null;
  return grossStrokes - received;
};

/** Net strokes for display, clamped to at least 1 — a shown score must be a legal score. */
export const displayNetStrokes = (
  grossStrokes: number | null | undefined,
  pickup: boolean,
  received: number
): number | null => {
  const net = netStrokes(grossStrokes, pickup, received);
  return net === null ? null : Math.max(1, net);
};

/**
 * Per-hole Stableford points from the UNCLAMPED net: 2 + par - net, floored at 0.
 * A hole-in-one with a stroke received on a par 3 is 5 points; deriving points from a
 * clamped net would wrongly give 4. Mirrors HandicapStrokes.StablefordPoints.
 */
export const stablefordPoints = (
  par: number,
  strokeIndex: number | null | undefined,
  playingHandicap: number,
  grossStrokes: number | null | undefined,
  pickup: boolean
): number => {
  const net = netStrokes(grossStrokes, pickup, strokesReceived(playingHandicap, strokeIndex));
  if (net === null) return 0;
  return Math.max(0, 2 + par - net);
};
